import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  getGetInventoryVehicleDocumentsQueryKey,
  useSaveInventoryVehicleDocument,
} from '@/api/generated/inventory-vehicles/inventory-vehicles';
import {
  buildVehicleDocumentFormSchema,
  emptyVehicleDocumentFormValues,
  toVehicleDocumentRequestBody,
  type VehicleDocumentFormValues,
} from './vehicleDocumentFormSchema';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  DocumentTypeCbId: 'documentType',
  Number: 'number',
  IssuedOn: 'issuedOn',
  ValidTo: 'validTo',
  Files: 'files',
};

interface UseVehicleDocumentFormOptions {
  vehicleId: number;
  initialValues: VehicleDocumentFormValues;
  /** Voláno po úspěšném uložení — typicky zavře dialog a zobrazí toast. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulář dialogu dokumentu: Zod + save mutace + mapování BE chyb. `submit()` = úspěch uložení.
 */
export function useVehicleDocumentForm({
  vehicleId,
  initialValues,
  onSaved,
  onError,
}: UseVehicleDocumentFormOptions) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const schema = useMemo(() => buildVehicleDocumentFormSchema(t), [t]);
  const saveMutation = useSaveInventoryVehicleDocument();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyVehicleDocumentFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      try {
        await saveMutation.mutateAsync({
          vehicleId,
          data: toVehicleDocumentRequestBody(value),
        });
        // Detail vozu dokumenty nevrací a seznam je nezobrazuje — stačí klíč dokumentů.
        await queryClient.invalidateQueries({
          queryKey: getGetInventoryVehicleDocumentsQueryKey(vehicleId),
        });
        successRef.current = true;
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return { form, submit, isSubmitting: saveMutation.isPending };
}

/** Typ instance TanStack Form dialogu dokumentu (pro propsy podřízených komponent). */
export type VehicleDocumentFormApi = ReturnType<typeof useVehicleDocumentForm>['form'];
